/**
 * Error display component for showing playback errors to users
 * Extends media-chrome patterns for proper integration with media-controller
 */
import { t } from 'media-chrome/utils/i18n.js';
import type { TranslateKeys } from '../lang/en.ts';

/**
 * Custom element that displays player errors as a fullscreen overlay.
 * Uses slot="centered-chrome" with noautohide to ensure visibility.
 *
 * @example
 * <bunny-error-display slot="centered-chrome" noautohide></bunny-error-display>
 */
class BunnyErrorDisplay extends HTMLElement {
  static get observedAttributes() {
    return ['visible'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.#render();
  }

  connectedCallback() {
    // Listen on media-controller for bubbled events from bunny-stream-video
    const mediaController = this.closest('media-controller');
    if (mediaController) {
      mediaController.addEventListener('playererror', this.#handleError);
    }

    // Also listen at document level for events that might not bubble through shadow DOM
    document.addEventListener('playererror', this.#handleError);
  }

  disconnectedCallback() {
    const mediaController = this.closest('media-controller');
    if (mediaController) {
      mediaController.removeEventListener('playererror', this.#handleError);
    }
    document.removeEventListener('playererror', this.#handleError);
  }

  #render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: none;
          position: absolute;
          inset: 0;
          justify-content: center;
          align-items: center;
          background-color: rgba(0, 0, 0, 0.8);
          color: #ccc;
          font-size: 14px;
          line-height: 1.5;
          text-align: center;
          padding: 20px;
          box-sizing: border-box;
        }

        :host([visible]) {
          display: flex;
        }

        .error-text {
          max-width: 80%;
        }
      </style>
      <div class="error-text" part="text"></div>
    `;
  }

  #handleError = (event: Event) => {
    const customEvent = event as CustomEvent<{
      type: string;
      code: string;
      message: string;
    }>;

    if (!customEvent.detail?.message) return;

    const { message } = customEvent.detail;
    const translatedMessage = t(message as TranslateKeys);

    const textEl = this.shadowRoot?.querySelector('.error-text');
    if (textEl) {
      textEl.textContent = translatedMessage || message;
    }

    this.setAttribute('visible', '');
  };

  hide() {
    this.removeAttribute('visible');
  }

  show(message: string) {
    const textEl = this.shadowRoot?.querySelector('.error-text');
    if (textEl) {
      textEl.textContent = message;
    }
    this.setAttribute('visible', '');
  }
}

customElements.define('bunny-error-display', BunnyErrorDisplay);

export default BunnyErrorDisplay;
